import React, { useState } from 'react';
import { Workflow, WorkflowRun } from '../../../types/workflow';
import { RunDetailDrawer } from '../RunDetailDrawer';

const STATUS_BG: Record<string, { bg: string; color: string }> = {
  succeeded: { bg: '#E9F6F6', color: '#0F6E56' },
  failed:    { bg: '#FEE2E2', color: '#b91c1c' },
  running:   { bg: '#E0F2FE', color: '#075985' },
  retrying:  { bg: '#FEF3C7', color: '#92400e' },
};

function fmtDur(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

interface Props {
  workflow: Workflow;
  extraRuns: WorkflowRun[];
}

export function RunsTab({ workflow, extraRuns }: Props) {
  const [selected, setSelected] = useState<WorkflowRun | null>(null);
  const all = [...extraRuns, ...workflow.runs];

  return (
    <>
      <section className="bg-white border border-trim rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-trim flex items-center justify-between">
          <h3 className="text-sm font-semibold text-ink">Runs</h3>
          <span className="text-xs text-muted">{all.length} runs</span>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-trim bg-wash">
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Run ID</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Started</th>
              <th className="text-right px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Duration</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Status</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Trigger</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Initiated by</th>
              <th className="text-right px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Retries</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Failed node</th>
              <th className="text-left px-5 py-2 text-xs font-semibold uppercase tracking-wide text-muted">Trace</th>
            </tr>
          </thead>
          <tbody>
            {all.map(r => {
              const st = STATUS_BG[r.status] || STATUS_BG.succeeded;
              return (
                <tr
                  key={r.run_id}
                  className="border-b border-wash last:border-0 hover:bg-[#F9FAFB] cursor-pointer"
                  onClick={() => setSelected(r)}
                >
                  <td className="px-5 py-2.5 font-mono text-xs text-navy">{r.run_id}</td>
                  <td className="px-5 py-2.5 text-muted text-xs">{new Date(r.started_at).toLocaleString()}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-xs text-copy">{fmtDur(r.duration_ms)}</td>
                  <td className="px-5 py-2.5">
                    <span className="px-2 py-0.5 rounded-full text-xs font-medium" style={{ background: st.bg, color: st.color }}>{r.status}</span>
                  </td>
                  <td className="px-5 py-2.5 text-copy text-xs">{r.trigger_source}</td>
                  <td className="px-5 py-2.5 text-copy text-xs">{r.initiated_by}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-xs">{r.retry_count}</td>
                  <td className="px-5 py-2.5 text-muted text-xs">{r.failed_node || '—'}</td>
                  <td className="px-5 py-2.5 font-mono text-[10px] text-muted">{r.trace_id}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </section>

      {selected && <RunDetailDrawer run={selected} onClose={() => setSelected(null)} />}
    </>
  );
}
